import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import { tagged } from "@mirage/service-logging";
import * as syncChannelRecommendationsJob from "@mirage/service-typeahead-search/service/jobs/sync-channel-recommendations";
import * as syncOmniboxRecommendationsJob from "@mirage/service-typeahead-search/service/jobs/sync-omnibox-recommendations";
import * as syncPeopleRecommendationsJob from "@mirage/service-typeahead-search/service/jobs/sync-people-recommendations";
import * as syncRecommendationsJob from "@mirage/service-typeahead-search/service/jobs/sync-recommendations";
import {
  cancelSyncSuggestedQueryScore,
  cancelSyncWeights,
  startSyncSuggestedQueryScore,
  startSyncWeights,
} from "@mirage/service-typeahead-search/service/scoring";
import search from "@mirage/service-typeahead-search/service/search";
import {
  CacheKey,
  TypeaheadCache,
} from "@mirage/service-typeahead-search/service/typeahead-cache";

import type { LogoutServiceConsumerContract } from "@mirage/service-logout";
import type { PersonOrgChart } from "@mirage/service-people/types";
import type { PersistentCacheShape } from "@mirage/service-typeahead-search/service/typeahead-cache";
import type {
  PeopleSyncResult,
  typeahead as typeahead_,
} from "@mirage/service-typeahead-search/service/types";
import type { PreviousQuery } from "@mirage/shared/search/cache-result";
import { PeopleRecommendation } from "@mirage/shared/search/people-recommendations";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import type { ChannelForFilter } from "@mirage/shared/search/search-filters";
import type { RecentSectionTrack } from "@mirage/shared/search/section-tracking-record";
import type { URLShortcut } from "@mirage/shared/search/url-shortcut";
import type { KVStorage } from "@mirage/storage";
import type { Observable } from "rxjs";
import { pipelineInMemoryCache } from "./utils/in-memory-cache";

const logger = tagged("service-typeahead-search");

export type Service = {
  cacheRecentQueries(queries: string[]): Promise<void>;

  getRecentQueries(): Promise<PreviousQuery[]>;
  getRecommendations(): Promise<Recommendation[]>;
  getChannelRecommendations(): Promise<ChannelForFilter[]>;
  getOrgChart(): Promise<PersonOrgChart[]>;
  getPeopleRecommendations(): Promise<PeopleRecommendation[]>;

  clear(key: CacheKey): Promise<void>;
  tearDown(): Promise<void>;
  remove(previousQuery: PreviousQuery): Promise<void>;

  search(
    query: string,
    mode: typeahead_.Mode,
    config: typeahead_.Config,
  ): Observable<{
    results: typeahead_.ScoredResult[];
    effectiveQuery: string;
  }>;

  startSyncRecommendations(): void;
  cancelSyncRecommendations(): void;

  startSyncPeopleRecommendations(): void;
  cancelSyncPeopleRecommendations(): void;
  getLastPeopleRecommendationsSyncMs(): Promise<number | undefined>;
  syncPeopleRecommendations(): Promise<PeopleSyncResult>;

  startSyncChannelRecommendations(): void;
  cancelSyncChannelRecommendations(): void;

  startSyncOmniboxRecommendations(): void;
  cancelSyncOmniboxRecommendations(): void;

  startSyncWeights(): void;
  cancelSyncWeights(): void;

  startSyncSuggestedQueryScore(): void;
  cancelSyncSuggestedQueryScore(): void;

  registerHit(uuid: string): Promise<void>;

  addURLShortcutToCache(shortcut: URLShortcut): Promise<void>;

  trackSectionInteraction(
    sectionId: typeahead_.SectionId | undefined,
    trackPayload: RecentSectionTrack,
  ): Promise<void>;
};

export default function typeahead(
  adapter: KVStorage<PersistentCacheShape>,
  auxiliarySources: typeahead_.PredicateSubject<typeahead_.SourceId>[] = [],
  logoutService: LogoutServiceConsumerContract,
) {
  const cache = new TypeaheadCache(adapter);

  async function cacheRecentQueries(queries: string[]): Promise<void> {
    await cache.cacheRecentQueries(queries);
  }

  async function getRecentQueries(): Promise<PreviousQuery[]> {
    return cache.all(CacheKey.RecentQueries) as PreviousQuery[];
  }

  async function getRecommendations(): Promise<Recommendation[]> {
    return cache.all(CacheKey.Recommendations) as Recommendation[];
  }

  async function getChannelRecommendations(): Promise<ChannelForFilter[]> {
    return cache.all(CacheKey.ChannelRecommendations) as ChannelForFilter[];
  }

  async function getPeopleRecommendations(): Promise<PeopleRecommendation[]> {
    return cache.all(CacheKey.PeopleRecommendations) as PeopleRecommendation[];
  }

  async function getOrgChart(): Promise<PersonOrgChart[]> {
    /*
    const lastPeopleRecommendationsSyncMs =
      await cache.getLastPeopleRecommendationsSyncMs();

    if (
      shouldSyncRecommendations(
        lastPeopleRecommendationsSyncMs,
        "people-recommendations",
        syncPeopleRecommendationsJob.SYNC_INTERVAL_MS,
      )
    ) {
      // If cache is stale, we need to clear birthday data from people cache
      // since birthday is considered sensitive data
      await cache.clearBirthdaysFromOrgChart();
    }
    */

    return cache.all(CacheKey.OrgChart) as PersonOrgChart[];
  }

  async function clear(key: CacheKey) {
    cache.clear(key);
  }

  // used to reset the cache when the user logs out
  async function tearDown() {
    await cache.tearDown();
  }

  async function remove(previousQuery: PreviousQuery): Promise<void> {
    await cache.remove(previousQuery);
    pipelineInMemoryCache.flush();
  }

  async function registerHit(uuid: string): Promise<void> {
    return cache.registerHit(uuid, Date.now());
  }

  async function trackSectionInteraction(
    sectionId: typeahead_.SectionId | undefined,
    trackPayload: RecentSectionTrack,
  ): Promise<void> {
    pipelineInMemoryCache.flush();
    return cache.trackSectionInteraction(sectionId, trackPayload);
  }

  async function addURLShortcutToCache(shortcut: URLShortcut): Promise<void> {
    return cache.addURLShortcutToCache(shortcut);
  }

  logoutService.registerLogoutCallback(ServiceId.TYPEAHEAD_SEARCH, async () => {
    logger.debug("Handling logout in typehaead search service");
    await tearDown();
    logger.debug("Done handling logout in typeahead search service");
  });

  return services.provide<Service>(
    ServiceId.TYPEAHEAD_SEARCH,
    {
      cacheRecentQueries,
      getRecentQueries,
      getRecommendations,
      getChannelRecommendations,
      getOrgChart,
      getPeopleRecommendations,
      clear,
      tearDown,
      remove,
      search: (query, mode, config) =>
        search(query, mode, cache, auxiliarySources, config),
      startSyncRecommendations: () => syncRecommendationsJob.start(cache),
      cancelSyncRecommendations: () => syncRecommendationsJob.cancel(),
      startSyncPeopleRecommendations: () =>
        syncPeopleRecommendationsJob.start(cache),
      getLastPeopleRecommendationsSyncMs: () =>
        cache.getLastPeopleRecommendationsSyncMs(),
      syncPeopleRecommendations: () =>
        syncPeopleRecommendationsJob.sync(cache, true),
      cancelSyncPeopleRecommendations: () =>
        syncPeopleRecommendationsJob.cancel(),
      startSyncChannelRecommendations: () =>
        syncChannelRecommendationsJob.start(cache),
      cancelSyncChannelRecommendations: () =>
        syncChannelRecommendationsJob.cancel(),
      startSyncOmniboxRecommendations: () =>
        syncOmniboxRecommendationsJob.start(cache),
      cancelSyncOmniboxRecommendations: () =>
        syncOmniboxRecommendationsJob.cancel(),
      startSyncWeights,
      cancelSyncWeights,
      startSyncSuggestedQueryScore,
      cancelSyncSuggestedQueryScore,
      registerHit,
      addURLShortcutToCache,
      trackSectionInteraction,
    },
    [ServiceId.DBX_API, ServiceId.EXPERIMENTATION],
  );
}
